import { ReactNode, useEffect, useRef, useState } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { STORAGE_KEYS } from '@/shared/constants'
import { isTokenValid, isAuthTokenValid, isRefreshTokenValid, setAuthTokens, clearAuthStorage } from '@/shared/services/http'
import { authApi } from '@/features/auth/api/auth.api'
import { LoginCredentials } from '@/features/auth/types'
import { ConfirmDialog } from '@/shared/components/ConfirmDialog/ConfirmDialog'

interface ProtectedRouteProps {
    children: ReactNode
}

export default function ProtectedRoute({ children }: ProtectedRouteProps) {
    const location = useLocation()
    const [authChecked, setAuthChecked] = useState(false)
    const [authenticated, setAuthenticated] = useState(false)
    const [showExpiryWarning, setShowExpiryWarning] = useState(false)
    const [countdown, setCountdown] = useState(10)
    const warningTimeoutRef = useRef<number | null>(null)

    const clearWarningTimeout = () => {
        if (warningTimeoutRef.current != null) {
            window.clearTimeout(warningTimeoutRef.current)
            warningTimeoutRef.current = null
        }
    }

    const getAccessTokenExpiresAt = () => {
        const found = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN_EXPIRES_AT)
        const num = Number(found)
        return Number.isNaN(num) ? null : num
    }

    const logout = () => {
        clearWarningTimeout()
        clearAuthStorage()
        setAuthenticated(false)
        setAuthChecked(true)
        window.location.href = '/'
    }

    const refreshByCredentials = async () => {
        const credsRaw = localStorage.getItem(STORAGE_KEYS.LOGIN_CREDENTIALS)
        if (!credsRaw) {
            logout()
            return
        }

        const credentials = JSON.parse(credsRaw) as LoginCredentials
        try {
            const loginResult = await authApi.login(credentials)

            const accessToken = loginResult.data.tokens.access_token
            const refreshToken = (loginResult.data.tokens as any).refresh_token
            const expiresIn = loginResult.data.tokens.expires_in
            const refreshExpiresIn = loginResult.data.tokens.refresh_expires_in

            setAuthTokens(accessToken, expiresIn, refreshToken, refreshExpiresIn)
            setAuthenticated(true)
            setShowExpiryWarning(false)
            setCountdown(10)
            clearWarningTimeout()

            // restart warning timeline
            scheduleWarning()
        } catch (error) {
            logout()
        }
    }

    const scheduleWarning = () => {
        clearWarningTimeout()
        const expireAt = getAccessTokenExpiresAt()
        if (!expireAt) {
            if (!isRefreshTokenValid()) logout()
            return
        }

        const timeLeft = expireAt - Date.now()
        if (timeLeft <= 0) {
            if (isRefreshTokenValid()) {
                setShowExpiryWarning(true)
                setCountdown(0)
            } else {
                logout()
            }
            return
        }

        if (timeLeft <= 10_000) {
            setShowExpiryWarning(true)
            setCountdown(Math.max(0, Math.ceil(timeLeft / 1000)))
            return
        }

        clearWarningTimeout()
        warningTimeoutRef.current = window.setTimeout(() => {
            warningTimeoutRef.current = null
            setShowExpiryWarning(true)
            setCountdown(10)
        }, timeLeft - 10_000)
    }

    useEffect(() => {
        const accessToken = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN)

        if (accessToken && isTokenValid(accessToken) && isAuthTokenValid()) {
            setAuthenticated(true)
            setAuthChecked(true)
            scheduleWarning()
            return
        }

        if (isRefreshTokenValid()) {
            setAuthenticated(true)
            setAuthChecked(true)
            scheduleWarning()
            return
        }

        setAuthenticated(false)
        setAuthChecked(true)

        return () => {
            clearWarningTimeout()
        }
    }, [])

    useEffect(() => {
        if (!showExpiryWarning) return

        const interval = window.setInterval(() => {
            setCountdown(prev => {
                if (prev <= 1) {
                    clearInterval(interval)
                    logout()
                    return 0
                }
                return prev - 1
            })
        }, 1000)

        return () => window.clearInterval(interval)
    }, [showExpiryWarning])

    if (!authChecked) {
        return <div>Checking session...</div>
    }

    if (!authenticated) {
        return <Navigate to="/" state={{ from: location }} replace />
    }

    return (
        <>
            {children}
            <ConfirmDialog
                open={showExpiryWarning}
                title="Session expiring soon"
                message={`Your session expires in ${countdown}s. Do you want to continue your session?`}
                confirmText="Continue session"
                cancelText="Logout now"
                onConfirm={refreshByCredentials}
                onCancel={logout}
                onOpenChange={(open) => {
                    if (!open) {
                        setShowExpiryWarning(false)
                    }
                }}
            />
        </>
    )
}


